import { useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { legalNextVisibilities, type PickClosedReason, type PickDetail, type PickEvent, type PickVisibility } from "@alpha/shared";
import { api, errorText } from "../api";
import { PositionSizeHelper } from "../components/PositionSizeHelper";
import { Chip, ErrorBox, Loading, PageHeader, signClass } from "../components/ui";
import { formatDateTime } from "../env";
import { useMe } from "../me";
import { useAsync } from "../useAsync";

const REASONS: { value: PickClosedReason; label: string }[] = [
  { value: "target_hit", label: "Target hit" },
  { value: "stop_hit", label: "Stop hit" },
  { value: "thesis_invalidated", label: "Thesis invalidated" },
  { value: "time_expiry", label: "Time expiry" },
  { value: "manual", label: "Closed manually" },
];

const TRANSITION_LABEL: Record<PickVisibility, string> = {
  private: "Make private",
  internal: "Share internally",
  public: "Make public",
};

export function describeDiff(diff: Record<string, unknown>): string[] {
  return Object.entries(diff).map(([k, raw]) => {
    const short = (x: unknown) => {
      const s = typeof x === "string" ? x : JSON.stringify(x);
      return s.length > 120 ? `${s.slice(0, 117)}...` : s;
    };
    if (raw && typeof raw === "object" && "to" in raw) {
      const { from, to } = raw as { from: unknown; to: unknown };
      return from === null || from === undefined ? `${k}: ${short(to)}` : `${k}: ${short(from)} to ${short(to)}`;
    }
    return `${k}: ${short(raw)}`;
  });
}

export function PickDetailPage() {
  const { id = "" } = useParams();
  const me = useMe();
  const pick = useAsync((s) => api.get<PickDetail>(`/v1/picks/${id}`, s), [id]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (pick.loading) return <Loading />;
  if (pick.error || !pick.data) return <ErrorBox error={pick.error} onRetry={pick.reload} />;
  const p = pick.data;
  const isAuthor = p.authorId === me.id;
  const canTransition = (isAuthor || me.role === "admin") && p.status !== "closed";
  const next = canTransition ? legalNextVisibilities(p.visibility) : [];

  async function transition(to: PickVisibility) {
    if (to === "public" && !window.confirm("Make this pick public? A public pick cannot be edited or deleted, only closed with a reason.")) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post(`/v1/picks/${p.id}/visibility`, { visibility: to, version: p.version });
      pick.reload();
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <PageHeader title={`${p.ticker} pick`}>
        <Chip value={p.direction} />
        <Chip value={p.visibility} />
        <Chip value={p.status} />
      </PageHeader>
      <div className="split">
        <div className="grow stack">
          <div className="card">
            <dl className="kv wide">
              <dt>Entry</dt>
              <dd className="num">{p.entry}</dd>
              <dt>Target</dt>
              <dd className="num">{p.target}</dd>
              <dt>Stop</dt>
              <dd className="num">{p.stop}</dd>
              <dt>Risk per trade</dt>
              <dd className="num">{p.riskPerTradePct}%</dd>
              <dt>Author</dt>
              <dd>{p.authorName}</dd>
              <dt>Created</dt>
              <dd>{formatDateTime(p.createdAt)}</dd>
              {p.publishedAt && (
                <>
                  <dt>Published</dt>
                  <dd>{formatDateTime(p.publishedAt)}</dd>
                </>
              )}
              {p.closedAt && (
                <>
                  <dt>Closed</dt>
                  <dd>
                    {formatDateTime(p.closedAt)}
                    {p.closedReason ? ` (${p.closedReason.replace(/_/g, " ")})` : ""}
                    {p.outcomePct ? (
                      <>
                        , outcome <span className={`num ${signClass(Number(p.outcomePct))}`}>{p.outcomePct}%</span>
                      </>
                    ) : (
                      ""
                    )}
                  </dd>
                </>
              )}
            </dl>
            <h3>Thesis</h3>
            <p className="thesis">{p.thesis}</p>
            <div className="row">
              <Link className="btn" to={`/chart?instrument=${p.instrumentId}`}>
                Open chart
              </Link>
              {isAuthor && p.status === "draft" && (
                <Link className="btn" to={`/picks/${p.id}/edit`}>
                  Edit
                </Link>
              )}
              {next.map((v) => (
                <button key={v} type="button" className="btn" disabled={busy} onClick={() => void transition(v)}>
                  {TRANSITION_LABEL[v]}
                </button>
              ))}
              {(isAuthor || me.role === "admin") && p.status !== "closed" && <CloseDialog pick={p} onClosed={pick.reload} />}
            </div>
            {actionError && (
              <p role="alert" className="error">
                {actionError}
              </p>
            )}
          </div>

          <div className="card">
            <h3>Timeline</h3>
            <ol className="timeline">
              {p.events.map((e: PickEvent) => (
                <li key={e.id}>
                  <div className="row between">
                    <strong>{e.type.replace(/_/g, " ")}</strong>
                    <span className="muted num">{formatDateTime(e.createdAt)}</span>
                  </div>
                  <div className="muted">by {e.actorName}</div>
                  <ul className="diff">
                    {describeDiff(e.diff).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          </div>
        </div>
        <div className="narrow">
          <PositionSizeHelper entry={p.entry} stop={p.stop} target={p.target} riskPct={p.riskPerTradePct} />
        </div>
      </div>
    </section>
  );
}

function CloseDialog({ pick, onClosed }: { pick: PickDetail; onClosed: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [reason, setReason] = useState<PickClosedReason>("manual");
  const [outcome, setOutcome] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/v1/picks/${pick.id}/close`, {
        reason,
        version: pick.version,
        ...(outcome.trim() ? { outcomePct: outcome.trim() } : {}),
      });
      ref.current?.close();
      onClosed();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="btn danger" onClick={() => ref.current?.showModal()}>
        Close pick
      </button>
      <dialog ref={ref} className="dialog" aria-labelledby="close-title">
        <form onSubmit={submit} className="stack">
          <h3 id="close-title">Close {pick.ticker} pick</h3>
          <p className="muted">Closing is final and is recorded with the reason. Public picks can only leave the journal this way.</p>
          <label>
            Reason
            <select value={reason} onChange={(e) => setReason(e.target.value as PickClosedReason)}>
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Outcome percent (optional)
            <input inputMode="decimal" value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="for example 6.5 or -3.2" />
          </label>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <div className="row end">
            <button type="button" className="btn" onClick={() => ref.current?.close()}>
              Cancel
            </button>
            <button type="submit" className="btn danger" disabled={busy}>
              {busy ? "Closing..." : "Close pick"}
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}
